import axios from 'axios';

export type SocialProvider = 'Google' | 'SignInWithApple';

const PKCE_VERIFIER_KEY = 'social_auth_pkce_verifier';
const PKCE_STATE_KEY = 'social_auth_pkce_state';

function base64UrlEncode(bytes: Uint8Array): string {
  const binary = String.fromCharCode(...bytes);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function generateRandomString(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

export function isSocialAuthConfigured(): boolean {
  return Boolean(import.meta.env.VITE_COGNITO_DOMAIN && import.meta.env.VITE_COGNITO_REDIRECT_URI);
}

/** Kicks off the Cognito Hosted UI redirect for Google/Apple; same handler for login and signup. */
export async function startSocialSignIn(provider: SocialProvider): Promise<void> {
  if (!isSocialAuthConfigured()) {
    throw new Error('Social sign-in is not configured for this environment.');
  }

  const verifier = generateRandomString(64);
  const state = generateRandomString(32);
  const challenge = await generateCodeChallenge(verifier);

  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);

  const params = new URLSearchParams({
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    response_type: 'code',
    redirect_uri: import.meta.env.VITE_COGNITO_REDIRECT_URI,
    scope: 'openid email profile',
    identity_provider: provider,
    prompt: provider === 'SignInWithApple' ? 'login' : 'select_account',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
}

/** Reads and clears the PKCE verifier/state stashed before the Hosted UI redirect. */
export function consumeStoredPkceState(): { verifier: string | null; state: string | null } {
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  const state = sessionStorage.getItem(PKCE_STATE_KEY);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  return { verifier, state };
}

export interface CognitoTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

export async function exchangeCodeForTokens(code: string, codeVerifier: string): Promise<CognitoTokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: import.meta.env.VITE_COGNITO_CLIENT_ID,
    client_secret: import.meta.env.VITE_COGNITO_CLIENT_SECRET_ID,
    code,
    redirect_uri: import.meta.env.VITE_COGNITO_REDIRECT_URI,
    code_verifier: codeVerifier,
  });

  const { data } = await axios.post<CognitoTokenResponse>(
    `https://${import.meta.env.VITE_COGNITO_DOMAIN}/oauth2/token`,
    body.toString(),
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } },
  );
  return data;
}
